import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../components/web3/context/AuthContext";
import {
  getMcpConfig,
  getMcpSpending,
  listMcpKeys,
  getMcpAudit,
  updateMcpConfig,
} from "../../api/mcpApi";
import { Skeleton } from "./shared";
import ApiKeysSection from "./ApiKeys/ApiKeysSection";
import ConfigSection from "./Config/ConfigSection";
import ActivitySection from "./Activity/ActivitySection";
import SetupSection from "./Setup/SetupSection";
import OverviewSection from "./Overview/OverviewSection";
import UseCasesSection from "./UseCases/UseCasesSection";
import ReactGA from "react-ga4";
import { toast } from "react-hot-toast";

// Normalize backend snake_case config into the frontend's expected shape
function normalizeConfig(raw) {
  const cfg = raw?.config || raw;
  const allowedChains = cfg.allowed_chains
    ? Object.entries(cfg.allowed_chains)
        .filter(([, v]) => v?.enabled)
        .map(([k]) => k.toLowerCase())
    : [];
  const allowedTokens = cfg.allowed_tokens?.tokens || [];
  const tokenMode = cfg.allowed_tokens?.whitelist_mode || "allow_any";
  const sl = cfg.spending_limits || {};
  const spendingLimits = {
    perTransaction: sl.per_transaction_usd ?? 50,
    dailyLimit: sl.daily_usd ?? 200,
    sessionLimit: sl.session_usd ?? 100,
    autoApproveUnder: sl.require_approval_above_usd ?? 10,
  };
  const features = cfg.features || {};
  const pp = cfg.payment_protocols || {};
  const protocols = {
    x402: { enabled: pp.x402?.enabled ?? true, autoApproveThreshold: pp.x402?.auto_approve_max_usd ?? 1 },
    mpp: { enabled: pp.mpp?.enabled ?? true, autoApproveThreshold: pp.mpp?.auto_approve_max_usd ?? 5, method: pp.mpp?.preferred_method || "tempo" },
  };
  const ar = cfg.address_rules || {};
  const addressRules = {
    blocklistEnabled: ar.blocklist_enabled ?? false,
    blocklist: ar.blocklist || [],
    allowlistOnly: ar.allowlist_enabled ?? false,
    allowlist: ar.allowlist || [],
  };
  return {
    enabled: cfg.mcp_enabled ?? true,
    allowedChains,
    allowedTokens,
    tokenMode,
    spendingLimits,
    features,
    protocols,
    addressRules,
  };
}

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "advanced", label: "Advanced" },
  { id: "activity", label: "Activity" },
  { id: "use-cases", label: "Use Cases" },
  { id: "setup", label: "Setup" },
];

export default function MCPPage() {
  const { isLoggedIn } = useAuth();

  const [activeTab, setActiveTab] = useState("overview");
  const [keysLoading, setKeysLoading] = useState(true);
  const [configLoading, setConfigLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [spending, setSpending] = useState(null);
  const [keys, setKeys] = useState([]);
  const [selectedKeyPrefix, setSelectedKeyPrefix] = useState(null);
  const [auditEntries, setAuditEntries] = useState([]);
  const [headerHeight, setHeaderHeight] = useState(0);

  const updateHeaderHeight = useCallback(() => {
    const header = document.getElementById("sticky-menu");
    if (header) setHeaderHeight(header.offsetHeight);
  }, []);

  useEffect(() => {
    updateHeaderHeight();
    window.addEventListener("resize", updateHeaderHeight);
    return () => window.removeEventListener("resize", updateHeaderHeight);
  }, [updateHeaderHeight]);

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: "/mcp", title: "MCP Settings Page" });
  }, []);

  // Load keys first, then auto-select the first key
  const loadKeys = useCallback(async () => {
    if (!isLoggedIn) return;
    setKeysLoading(true);
    try {
      const data = await listMcpKeys();
      const keysList = data.keys || data || [];
      setKeys(keysList);
      // Auto-select first key if none selected
      if (keysList.length > 0 && !selectedKeyPrefix) {
        setSelectedKeyPrefix(keysList[0].prefix);
      }
    } catch {
      toast.error("Failed to load API keys");
    } finally {
      setKeysLoading(false);
    }
  }, [isLoggedIn, selectedKeyPrefix]);

  // Load config + spending for the selected key
  const loadConfig = useCallback(async () => {
    if (!isLoggedIn || !selectedKeyPrefix) {
      setConfigLoading(false);
      return;
    }
    setConfigLoading(true);
    try {
      const [configData, spendingData] = await Promise.allSettled([
        getMcpConfig(selectedKeyPrefix),
        getMcpSpending(),
      ]);
      if (configData.status === "fulfilled")
        setConfig(normalizeConfig(configData.value));
      if (spendingData.status === "fulfilled")
        setSpending(spendingData.value);
    } catch {
      toast.error("Failed to load config");
    } finally {
      setConfigLoading(false);
    }
  }, [isLoggedIn, selectedKeyPrefix]);

  // Load audit independently
  const loadAudit = useCallback(async () => {
    if (!isLoggedIn) return;
    setAuditLoading(true);
    getMcpAudit(50)
      .then((data) => setAuditEntries(data.entries || []))
      .catch(() => toast.error("Failed to load activity"))
      .finally(() => setAuditLoading(false));
  }, [isLoggedIn]);

  useEffect(() => { loadKeys(); }, [loadKeys]);
  useEffect(() => { loadConfig(); }, [loadConfig]);
  useEffect(() => { loadAudit(); }, [loadAudit]);

  // Optimistic config update for the selected key
  const handleConfigUpdate = useCallback(
    async (localUpdater, apiPayload) => {
      if (!selectedKeyPrefix) return;
      const prevConfig = config;
      setConfig((prev) => {
        const updates = localUpdater(prev);
        return { ...prev, ...updates };
      });
      try {
        await updateMcpConfig(apiPayload, selectedKeyPrefix);
      } catch {
        setConfig(prevConfig);
        toast.error("Failed to save setting");
      }
    },
    [config, selectedKeyPrefix]
  );

  const refreshKeys = useCallback(async () => {
    try {
      const data = await listMcpKeys();
      const keysList = data.keys || data || [];
      setKeys(keysList);
      // If selected key was revoked, select first available
      if (selectedKeyPrefix && !keysList.find((k) => k.prefix === selectedKeyPrefix)) {
        setSelectedKeyPrefix(keysList.length > 0 ? keysList[0].prefix : null);
      }
    } catch {
      toast.error("Failed to refresh keys");
    }
  }, [selectedKeyPrefix]);

  const refreshConfig = useCallback(async () => {
    if (!selectedKeyPrefix) return;
    try {
      const [configData, spendingData] = await Promise.all([
        getMcpConfig(selectedKeyPrefix),
        getMcpSpending(),
      ]);
      setConfig(normalizeConfig(configData));
      setSpending(spendingData);
    } catch {
      // silent
    }
  }, [selectedKeyPrefix]);

  const refreshAudit = useCallback(async () => {
    try {
      const data = await getMcpAudit(50);
      setAuditEntries(data.entries || []);
    } catch {
      toast.error("Failed to refresh activity");
    }
  }, []);

  const latestKeyPrefix =
    keys.length > 0
      ? keys.sort(
          (a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt)
        )[0]?.prefix
      : null;

  if (!isLoggedIn) {
    return (
      <div className="mcp-page" style={{ paddingTop: headerHeight }}>
        <div className="mcp-container">
          <div className="mcp-login-prompt">
            <h3>Sign in to manage AI agent access</h3>
            <p>Connect your wallet or sign in to configure MCP settings.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mcp-page" style={{ paddingTop: headerHeight }}>
      <div className="mcp-container">
        <div className="mcp-page-header">
          <h2 className="mcp-page-title">MCP Settings</h2>
          <p className="mcp-page-subtitle">
            Control center for AI agent integration
          </p>
        </div>

        {/* Page-level tabs */}
        <div className="mcp-page-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`mcp-page-tab ${
                activeTab === tab.id ? "mcp-page-tab-active" : ""
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Tab: Overview ─────────────────────────────────── */}
        {activeTab === "overview" && (
          <div className="mcp-sections">
            {keysLoading ? (
              <Skeleton lines={4} />
            ) : (
              <ApiKeysSection
                keys={keys}
                onKeysChanged={refreshKeys}
                loading={keysLoading}
              />
            )}

            {/* Key selector for config */}
            {keys.length > 0 && (
              <>
                <KeySelector
                  keys={keys}
                  selectedPrefix={selectedKeyPrefix}
                  onSelect={(prefix) => setSelectedKeyPrefix(prefix)}
                />
                {configLoading ? (
                  <Skeleton lines={3} />
                ) : (
                  <OverviewSection
                    config={config}
                    spending={spending}
                    onConfigUpdate={handleConfigUpdate}
                    onGoToAdvanced={() => setActiveTab("advanced")}
                  />
                )}
              </>
            )}
          </div>
        )}

        {/* ── Tab: Advanced ─────────────────────────────────── */}
        {activeTab === "advanced" && (
          <div className="mcp-sections">
            {keys.length > 0 ? (
              <>
                <KeySelector
                  keys={keys}
                  selectedPrefix={selectedKeyPrefix}
                  onSelect={(prefix) => setSelectedKeyPrefix(prefix)}
                />
                {configLoading ? (
                  <Skeleton lines={6} />
                ) : (
                  <ConfigSection
                    config={config}
                    spending={spending}
                    onConfigUpdate={handleConfigUpdate}
                    onConfigChanged={refreshConfig}
                  />
                )}
              </>
            ) : (
              <div className="mcp-section">
                <div className="mcp-empty-state">
                  <h4 className="mcp-empty-title">Generate an API key first</h4>
                  <p className="mcp-empty-desc">
                    Advanced settings become available after you create an API key.
                  </p>
                  <button
                    type="button"
                    className="mcp-btn mcp-btn-primary"
                    onClick={() => setActiveTab("overview")}
                  >
                    Go to Overview
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Tab: Activity ─────────────────────────────────── */}
        {activeTab === "activity" && (
          <div className="mcp-sections">
            {auditLoading ? (
              <Skeleton lines={3} />
            ) : (
              <ActivitySection
                entries={auditEntries}
                onRefresh={refreshAudit}
              />
            )}
          </div>
        )}

        {/* ── Tab: Use Cases ────────────────────────────────── */}
        {activeTab === "use-cases" && (
          <div className="mcp-sections">
            <UseCasesSection />
          </div>
        )}

        {/* ── Tab: Setup ────────────────────────────────────── */}
        {activeTab === "setup" && (
          <div className="mcp-sections">
            <SetupSection latestKey={latestKeyPrefix} />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Key Selector Component ─────────────────────────────────
function KeySelector({ keys, selectedPrefix, onSelect }) {
  return (
    <div className="mcp-key-selector">
      <label className="mcp-key-selector-label">Configure key:</label>
      <div className="mcp-key-selector-options">
        {keys.map((k) => {
          const prefix = k.prefix || k.keyPrefix || "";
          const isActive = prefix === selectedPrefix;
          return (
            <button
              key={prefix}
              type="button"
              className={`mcp-key-selector-btn ${isActive ? "mcp-key-selector-active" : ""}`}
              onClick={() => onSelect(prefix)}
            >
              <span className="mcp-key-selector-name">{k.name}</span>
              <span className="mcp-key-selector-wallet">
                #{k.wallet_index ?? k.walletIndex ?? 0}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
